import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MuiFormHelperText from '@mui/material/FormHelperText';
import MuiMenu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import { mergeSlotProps } from '@mui/material/utils';
import * as React from 'react';
import { Box, FlexBox } from '../box';
import { Button } from '../button';
import { useId } from '../hooks';
import { getIcon, IconArrowDropDown } from '../icon';
import { withPaperElevation } from '../paper';
import { Skeleton } from '../skeleton';
import { styled } from '../styled';
import { TextField } from '../textfield';
import { fieldErrorProps } from '../textfield/utils';
import { getPaletteColor } from '../theme';
import { Tooltip } from '../tooltip';
import { sentence, Typography } from '../typography';
import { withVersion } from '../utils';
const Root = styled(Box)(({ $wrapLabel }) => ({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    ...($wrapLabel && {
        flex: 1,
        minWidth: 0
    })
}));
const StyledButton = styled(Button)(({ theme, $hasError, $open, $variant = 'primary', $visualState = 'default', $wrapLabel }) => {
    const isPrimary = $variant === 'primary';
    const labelDefault = getPaletteColor(theme, `components.simple-select.${$variant}.closed.label-default`);
    const labelActive = getPaletteColor(theme, `components.simple-select.${$variant}.opened.label-active`);
    const borderColor = getPaletteColor(theme, `components.simple-select.${$variant}.border-active`);
    const closedBackground = getPaletteColor(theme, `components.simple-select.${$variant}.closed.bg`);
    const hoverBackground = getPaletteColor(theme, `components.simple-select.${$variant}.opened.bg-hover`);
    const closedRippleBackground = getPaletteColor(theme, `components.simple-select.${$variant}.closed.bg-ripple`);
    const openedBackground = getPaletteColor(theme, `components.simple-select.${$variant}.opened.bg`);
    const isClosedDefault = !$open && $visualState === 'default';
    const textColor = isPrimary && !isClosedDefault ? labelActive : labelDefault;
    const isHover = $visualState === 'hover';
    const isFocused = $visualState === 'focused';
    const isActive = $visualState === 'active';
    const focusBackground = $open ? openedBackground : closedRippleBackground;
    const resolvedBackgroundColor = (() => {
        if ($open) {
            return isHover ? hoverBackground : openedBackground;
        }
        if (isHover) {
            return hoverBackground;
        }
        if (isFocused) {
            return closedRippleBackground;
        }
        if (isActive) {
            return isPrimary ? openedBackground : closedBackground;
        }
        return closedBackground;
    })();
    const showBorder = $open || $hasError;
    const borderShadow = `inset 0 0 0 1px ${$hasError ? theme.palette.error.main : borderColor}`;
    return {
        minWidth: 0,
        height: 30,
        ...($wrapLabel
            ? {
                height: 'auto',
                minHeight: 30,
                whiteSpace: 'normal',
                textAlign: 'left'
            }
            : {}),
        boxSizing: 'border-box',
        paddingTop: theme.spacing(0.5),
        paddingRight: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(0.75),
        borderRadius: theme.shape.borderRadius,
        border: 'none',
        boxShadow: showBorder ? borderShadow : 'none',
        backgroundColor: resolvedBackgroundColor,
        color: textColor,
        lineHeight: '22px',
        letterSpacing: '0.46px',
        '&:hover': {
            boxShadow: showBorder ? borderShadow : 'none',
            backgroundColor: hoverBackground,
            color: isPrimary ? labelActive : textColor
        },
        '&.Mui-focusVisible': {
            boxShadow: showBorder ? borderShadow : 'none',
            backgroundColor: focusBackground
        },
        '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent'
        }
    };
});
const ButtonText = styled(Typography)(({ theme, $wrap }) => {
    const labelFontSize = theme.palette.components?.['simple-select']?.label;
    return {
        ...(labelFontSize ? { fontSize: Number(labelFontSize) } : {}),
        lineHeight: 1.2,
        ...($wrap && {
            overflowWrap: 'anywhere',
            textAlign: 'left'
        })
    };
});
const OpenStateIcon = styled(IconArrowDropDown)(({ $open }) => ({
    color: 'inherit',
    fontSize: 'inherit',
    ...($open && { transform: 'rotate(180deg)' })
}));
const StartAdornment = styled(FlexBox)({
    flexShrink: 0,
    fontSize: 20,
    '& svg': {
        fontSize: 'inherit'
    }
});
const OverlaySkeleton = styled(Skeleton)({
    position: 'absolute'
});
const HelperText = styled(MuiFormHelperText)(({ theme }) => ({
    padding: theme.spacing(0.25, 0.75, 0, 0.75)
}));
const StyledMenuItem = styled(MuiMenuItem)(({ theme, $highlighted }) => {
    const hoverBackground = theme.palette.action.hover;
    const selectedBackground = theme.palette.primary._states.selected;
    const selectedHighlightBackground = theme.palette.primary._states.focus;
    return {
        minHeight: 36,
        paddingTop: theme.spacing(0.75),
        paddingRight: theme.spacing(1.5),
        paddingBottom: theme.spacing(0.75),
        paddingLeft: theme.spacing(1.5),
        '&:hover': {
            backgroundColor: hoverBackground
        },
        '&.Mui-selected': {
            backgroundColor: selectedBackground
        },
        '&.Mui-selected:hover': {
            backgroundColor: selectedHighlightBackground
        },
        ...($highlighted
            ? {
                backgroundColor: hoverBackground,
                '&.Mui-selected': {
                    backgroundColor: selectedHighlightBackground
                },
                '&:hover': {
                    backgroundColor: hoverBackground
                },
                '&.Mui-selected:hover': {
                    backgroundColor: selectedHighlightBackground
                }
            }
            : {})
    };
});
const SearchContainer = Object.assign(styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper
})), {
    muiSkipListHighlight: true
});
const getFallbackOptionKey = (option, index) => {
    if (typeof option === 'string' || typeof option === 'number') {
        return String(option);
    }
    if (typeof option === 'boolean') {
        return option ? 'true' : 'false';
    }
    return String(index);
};
const isSearchInputLocalKey = (key) => key.length === 1 || ['Backspace', 'Delete', 'Home', 'End'].includes(key);
const areOptionsEqual = (props) => {
    const { getOptionKey, left, right } = props;
    if (getOptionKey) {
        return getOptionKey(left) === getOptionKey(right);
    }
    return left === right;
};
const getButtonLabel = (props) => {
    const { getOptionLabel, hasValue, label, multiple, placeholder, selectedValues, sentenceOptions, value } = props;
    if (hasValue) {
        if (multiple) {
            return selectedValues
                .map((selectedValue) => getOptionText(selectedValue, getOptionLabel, sentenceOptions))
                .join(', ');
        }
        const valueLabel = getOptionLabel?.(value) ?? String(value);
        return sentenceOptions ? sentence(valueLabel) : valueLabel;
    }
    if (label) {
        return label;
    }
    if (placeholder) {
        return sentence(placeholder);
    }
    return '';
};
const getOptionText = (option, getOptionLabel, sentenceOptions) => {
    const optionLabel = getOptionLabel?.(option) ?? String(option);
    return sentenceOptions ? sentence(optionLabel) : optionLabel;
};
const SimpleSelectImpl = React.forwardRef(function SimpleSelectImpl(props, ref) {
    const { buttonProps, className, 'data-role': dataRole, 'data-role-value': dataRoleValue, disabled, enableSearch = false, error, forceOpen = false, getOptionIcon, getOptionKey, getOptionLabel, helperText, label, menuProps, multiple, noOptionsText, options, placeholder, searchPlaceholderText, sentenceOptions = false, startIcon, skeleton, tooltip, tooltipProps, value, variant = 'primary', visualState, wrapButtonLabel = false } = props;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [searchValue, setSearchValue] = React.useState('');
    const [searchFocusRestoreKey, setSearchFocusRestoreKey] = React.useState(0);
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
    const buttonId = useId();
    const menuId = useId();
    const helperId = useId();
    const searchInputRef = React.useRef(null);
    const optionRefs = React.useRef([]);
    const selectedValues = multiple ? (value ?? []) : [];
    const hasValue = multiple
        ? selectedValues.length > 0
        : value !== null && value !== undefined;
    const open = forceOpen || Boolean(anchorEl);
    const { error: hasError, helperText: fieldHelperText } = fieldErrorProps(error, helperText);
    const describedBy = (fieldHelperText
        ? [buttonProps?.['aria-describedby'], helperId]
            .filter(Boolean)
            .join(' ')
        : buttonProps?.['aria-describedby']) || undefined;
    const buttonLabel = getButtonLabel({
        getOptionLabel,
        hasValue,
        label,
        multiple,
        placeholder,
        selectedValues,
        sentenceOptions,
        value: multiple ? undefined : value
    });
    const resolvedVisualState = visualState ?? (hasValue ? 'active' : 'default');
    const resolvedStartIcon = variant === 'primary' ? startIcon : undefined;
    const StartIcon = resolvedStartIcon
        ? getIcon(resolvedStartIcon)
        : undefined;
    const filteredOptions = React.useMemo(() => {
        if (!enableSearch) {
            return options;
        }
        const normalizedQuery = searchValue.trim().toLowerCase();
        if (!normalizedQuery) {
            return options;
        }
        return options.filter((option) => {
            const labelText = getOptionLabel?.(option) ?? String(option);
            return labelText.toLowerCase().includes(normalizedQuery);
        });
    }, [enableSearch, getOptionLabel, options, searchValue]);
    const closeMenu = React.useCallback(() => {
        setAnchorEl(null);
        setSearchValue('');
        setHighlightedIndex(-1);
    }, []);
    const focusSearchInput = React.useCallback(() => {
        searchInputRef.current?.focus();
    }, []);
    React.useEffect(() => {
        if (!open || !enableSearch) {
            return;
        }
        // eslint-disable-next-line ml-rules/prefer-using-for-disposable
        const timeout = setTimeout(focusSearchInput, 0);
        return () => clearTimeout(timeout);
    }, [enableSearch, focusSearchInput, open]);
    React.useEffect(() => {
        optionRefs.current = optionRefs.current.slice(0, filteredOptions.length);
    }, [filteredOptions.length]);
    React.useEffect(() => {
        if (!open || !enableSearch) {
            return;
        }
        if (filteredOptions.length === 0) {
            setHighlightedIndex(-1);
            return;
        }
        setHighlightedIndex((currentValue) => {
            if (currentValue < 0) {
                return 0;
            }
            return Math.min(currentValue, filteredOptions.length - 1);
        });
    }, [enableSearch, filteredOptions.length, open]);
    React.useEffect(() => {
        if (highlightedIndex < 0) {
            return;
        }
        optionRefs.current[highlightedIndex]?.scrollIntoView({
            block: 'nearest'
        });
    }, [highlightedIndex]);
    React.useEffect(() => {
        if (!open || !enableSearch || searchFocusRestoreKey === 0) {
            return;
        }
        // eslint-disable-next-line ml-rules/prefer-using-for-disposable
        const timeout = setTimeout(focusSearchInput, 0);
        return () => clearTimeout(timeout);
    }, [enableSearch, focusSearchInput, open, searchFocusRestoreKey]);
    const onToggle = (event) => {
        setAnchorEl((currentAnchor) => currentAnchor ? null : event.currentTarget);
    };
    const handleOptionSelection = (option, event) => {
        if (multiple) {
            const alreadySelected = selectedValues.some((item) => areOptionsEqual({
                getOptionKey,
                left: item,
                right: option
            }));
            const next = alreadySelected
                ? selectedValues.filter((item) => !areOptionsEqual({
                    getOptionKey,
                    left: item,
                    right: option
                }))
                : [...selectedValues, option];
            props.onChange?.(next, event, alreadySelected ? 'removeOption' : 'selectOption');
            if (enableSearch) {
                setSearchFocusRestoreKey((currentValue) => currentValue + 1);
            }
            return;
        }
        closeMenu();
        props.onChange?.(option, event, 'selectOption');
    };
    const handleSearchKeyDown = (event) => {
        if (isSearchInputLocalKey(event.key)) {
            event.stopPropagation();
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            closeMenu();
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            if (filteredOptions.length === 0) {
                return;
            }
            setHighlightedIndex((currentValue) => {
                if (event.key === 'ArrowDown') {
                    return currentValue < 0
                        ? 0
                        : Math.min(currentValue + 1, filteredOptions.length - 1);
                }
                return currentValue < 0
                    ? filteredOptions.length - 1
                    : Math.max(currentValue - 1, 0);
            });
            return;
        }
        if (event.key === 'Enter' && highlightedIndex >= 0) {
            event.preventDefault();
            event.stopPropagation();
            handleOptionSelection(filteredOptions[highlightedIndex], event);
        }
    };
    const handleSearchMouseDown = (event) => {
        event.stopPropagation();
    };
    const handleOptionMouseDown = (event) => {
        if (enableSearch && multiple) {
            event.preventDefault();
        }
    };
    const button = (_jsx(StyledButton, { ref: ref, id: buttonId, className: className, "data-role": dataRole ?? 'simple-select', "data-role-value": dataRoleValue, variant: "outlined", color: "inherit", size: buttonProps?.size ?? 'small', disabled: disabled || skeleton, onClick: onToggle, "aria-haspopup": "menu", "aria-expanded": open ? 'true' : undefined, "aria-controls": open ? menuId : undefined, "$hasError": hasError, "$open": open, "$variant": variant, "$visualState": resolvedVisualState, "$wrapLabel": wrapButtonLabel, ...buttonProps, "aria-describedby": describedBy, children: _jsxs(FlexBox, { gap: 0.5, vAlign: "center", minWidth: 0, children: [StartIcon && (_jsx(StartAdornment, { vAlign: "center", children: _jsx(StartIcon, { color: "inherit", filled: open, fontSize: "inherit" }) })), _jsx(ButtonText, { color: "inherit", fontWeight: "inherit", noWrap: !wrapButtonLabel, "$wrap": wrapButtonLabel, children: buttonLabel }), variant === 'primary' && (_jsx(FlexBox, { vAlign: "center", flexShrink: 0, fontSize: 20, children: _jsx(OpenStateIcon, { "$open": open, filled: open }) }))] }) }));
    const trigger = tooltip ? (_jsx(Tooltip, { title: tooltip, ...tooltipProps, children: button })) : (button);
    return (_jsxs(Root, { "$wrapLabel": wrapButtonLabel, children: [skeleton ? (_jsxs(Box, { position: "relative", width: "fit-content", children: [_jsx(OverlaySkeleton, { width: "100%", height: "100%" }), _jsx(Box, { visibility: "hidden", children: trigger })] })) : (trigger), _jsxs(MuiMenu, { id: menuId, anchorEl: anchorEl, open: Boolean(anchorEl), onClose: closeMenu, disableAutoFocusItem: menuProps?.disableAutoFocusItem ?? enableSearch, anchorOrigin: menuProps?.anchorOrigin ?? {
                    vertical: 'bottom',
                    horizontal: 'left'
                }, transformOrigin: menuProps?.transformOrigin ?? {
                    vertical: 'top',
                    horizontal: 'left'
                }, slotProps: {
                    ...menuProps?.slotProps,
                    paper: withPaperElevation(mergeSlotProps(menuProps?.slotProps?.paper, {
                        sx: { minWidth: 'max-content' }
                    }), 'L'),
                    list: mergeSlotProps(menuProps?.slotProps?.list, {
                        sx: {
                            padding: 0
                        }
                    })
                }, variant: menuProps?.variant ?? (enableSearch ? 'menu' : undefined), disableAutoFocus: menuProps?.disableAutoFocus, disableEnforceFocus: menuProps?.disableEnforceFocus, disableRestoreFocus: menuProps?.disableRestoreFocus, children: [enableSearch && (_jsx(SearchContainer, { onMouseDown: handleSearchMouseDown, children: _jsx(TextField, { inputRef: searchInputRef, size: "small", value: searchValue, placeholder: searchPlaceholderText, onChange: setSearchValue, onKeyDown: handleSearchKeyDown, slotProps: {
                                htmlInput: {
                                    role: 'combobox',
                                    'aria-autocomplete': 'list',
                                    'aria-controls': menuId,
                                    'aria-expanded': open,
                                    'aria-activedescendant': highlightedIndex >= 0
                                        ? `${menuId}-option-${highlightedIndex}`
                                        : undefined,
                                    sx: {
                                        width: '325px'
                                    }
                                }
                            } }) })), filteredOptions.length === 0 && (_jsx(StyledMenuItem, { disabled: true, dense: true, children: noOptionsText })), filteredOptions.map((option, index) => {
                        const optionKey = getOptionKey
                            ? getOptionKey(option)
                            : getFallbackOptionKey(option, index);
                        const selected = (() => {
                            if (!hasValue) {
                                return false;
                            }
                            if (multiple) {
                                return selectedValues.some((selectedValue) => areOptionsEqual({
                                    getOptionKey,
                                    left: selectedValue,
                                    right: option
                                }));
                            }
                            return areOptionsEqual({
                                getOptionKey,
                                left: value,
                                right: option
                            });
                        })();
                        const optionIcon = getOptionIcon?.(option);
                        const Icon = optionIcon
                            ? getIcon(optionIcon.icon)
                            : undefined;
                        const optionId = `${menuId}-option-${index}`;
                        return (_jsx(StyledMenuItem, { id: optionId, dense: true, selected: selected, "$highlighted": enableSearch && highlightedIndex === index, role: "menuitem", "data-role": "simple-select-option", "data-role-value": String(optionKey), ref: (element) => {
                                optionRefs.current[index] = element;
                            }, onMouseDown: handleOptionMouseDown, onClick: (event) => handleOptionSelection(option, event), children: _jsxs(FlexBox, { gap: 1, vAlign: "center", children: [Icon && (_jsx(Icon, { ...(optionIcon?.iconProps ?? {}) })), _jsx(Typography, { children: getOptionText(option, getOptionLabel, sentenceOptions) })] }) }, optionKey));
                    })] }), fieldHelperText && (_jsx(HelperText, { id: helperId, error: hasError, children: fieldHelperText }))] }));
});
export const SimpleSelect = withVersion(SimpleSelectImpl, '1.0.0');
